import { ActionCreatorWithPayload } from '@reduxjs/toolkit';

import {
  LubavSessionType,
  SolutionSessionId,
  SOLUTION_SESSION_IDS,
  LubavSupersession,
  LUBAV_SUPERSESSIONS,
  OFFICE_HOURS_SESSION_IDS,
  StructuredSessionId,
} from '@backrs_ui/grow-ui/src/models/models';

import { initialState as boxDataInitialState } from '../boxData/boxData';
import { initialState as coachLubavInitialState } from '../coachLubav/coachLubav';
import { initialState as solutionSessionInitialState } from '../solutionSession/solutionSession';
import { RootState } from '../store';

// helper to create a session-specific action
export const createSessionAction = <T>(
  actionCreator: ActionCreatorWithPayload<T>,
  sessionType: LubavSessionType | null,
) => {
  return (payload: T) => ({
    ...actionCreator(payload),
    sessionType,
  });
};

// helper to get session-specific state from the root state
export const getSessionState = (state: RootState, sessionType: LubavSessionType | null) => {
  // If the session doesn't exist yet, return initial state structure
  if (!sessionType || !state.sessionState.sessions[sessionType]) {
    return {
      coachLubav: { ...coachLubavInitialState, loading: false },
      boxData: boxDataInitialState,
      solutionSession: solutionSessionInitialState,
    };
  }

  return state.sessionState.sessions[sessionType];
};

// helper to get the active session type
export const getActiveLubavSession = (state: RootState) => {
  return state.sessionState.activeSessionType;
};

// helper to get the office hours subsession type
export const getActiveLubavSubsession = (state: RootState) => {
  return state.sessionState.officeHoursSubsessionType;
};

export const getPendingSessionSwitch = (state: RootState) => {
  return state.sessionState.pendingSessionSwitch;
};

export const getPendingStructuredChoice = (state: RootState) => {
  return state.sessionState.pendingStructuredChoice;
};

export const getPreviewedSession = (state: RootState) => {
  return state.sessionState.previewedSession;
};

export const getDisplayedSession = (state: RootState): LubavSessionType | null =>
  state.sessionState.previewedSession ?? state.sessionState.activeSessionType;

export const getLoadingSessionData = (state: RootState) => {
  return state.sessionState.loadingSessionData;
};

export const typeGuardForSolutionSessionId = (
  sessionName: LubavSessionType,
): sessionName is SolutionSessionId => {
  return Object.values(SOLUTION_SESSION_IDS).includes(sessionName as SolutionSessionId);
};

export const isShellManagedSession = (
  sessionName: LubavSessionType,
): sessionName is LubavSupersession | StructuredSessionId => {
  return (
    Object.values(LUBAV_SUPERSESSIONS).includes(sessionName as LubavSupersession) ||
    Object.values(OFFICE_HOURS_SESSION_IDS).includes(sessionName as StructuredSessionId)
  );
};
